import { Group } from '@mantine/core';
import type { LiteSecodaEntity } from '@repo/api-codegen';
import { isNil } from 'lodash-es';
import { useIntegrationPrefetched } from '../../../api';
import { PropertySelector } from '../../Properties/PropertySelector';
import TagPropertySelector from '../../Properties/TagPropertySelector';

interface AutomationJobAiOutputProperty {
	description?: string | null;
	definition?: string | null;
	pii?: boolean | null;
	tags?: string[] | null;
}

export interface AutomationJobAiOutputPropertySelectorProps {
	entity: LiteSecodaEntity;
	property: AutomationJobAiOutputProperty;
	onTagChange: (tags: string[]) => void;
	onPiiChange: (pii: boolean) => void;
}

export function AutomationJobAiOutputPropertySelector({
	entity,
	property,
	onTagChange,
	onPiiChange,
}: AutomationJobAiOutputPropertySelectorProps) {
	const { data: integration } = useIntegrationPrefetched({
		id: entity.integration?.id ?? '',
		options: {
			enabled: Boolean(entity.integration?.id),
		},
	});

	const handlePiiChange = (pii: string | number | boolean | string[]) => {
		if (typeof pii === 'boolean') {
			onPiiChange(pii);
		}
	};

	const handleTagChange = (tags: string[]) => {
		onTagChange(tags);
	};

	return (
		<Group spacing="3xs">
			{!isNil(property.pii) && (
				<PropertySelector
					selected={!!property.pii}
					type="single"
					value="pii"
					iconType="tabler"
					isViewerUser={false}
					readOnly={false}
					onChange={handlePiiChange}
				/>
			)}
			{property.tags && (
				<TagPropertySelector
					key={property.tags?.join(',') ?? 'tags'}
					initialValue={property.tags ?? []}
					placeholder="No tags"
					placeholderIconName="tag"
					forceVariant="tertiary"
					forceSourceTags={!!integration?.force_source_tags}
					entityIntegrationId={integration?.id}
					onChange={handleTagChange}
					withinPortal={false}
				/>
			)}
		</Group>
	);
}
